import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GoogleMap, MarkerF, InfoWindow, MarkerClusterer, Polyline } from '@react-google-maps/api';
import { Badge, Button, Spinner, Row, Col, ListGroup, Alert, Form, InputGroup } from 'react-bootstrap';
import { Navigation, Clock, User, Car, Maximize, AlertTriangle, CheckCircle, Users, Phone, MessageCircle, MapPin, Filter, Search, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import apiClient from '../../api';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'react-toastify';

const mapContainerStyle = { width: '100%', height: 'calc(100vh - 180px)' };
const LA_SERENA = { lat: -29.9027, lng: -71.2519 };

// SVG de Ícono de Auto
const carIconPath = "M19 17h2c.6 0 1-1.4 1-2.5V11c0-1.1-.4-2.1-1.3-2.7l-2.4-1.6c-.8-.5-1.8-.7-2.7-.7H8.4c-.9 0-1.9.2-2.7.7L3.3 8.3C2.4 8.9 2 9.9 2 11v3.5c0 1.1.4 2.5 1 2.5h2M7 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4ZM17 17a2 2 0 1 0 0 4 2 2 0 0 0 0-4ZM5 11h14M11 7v4";

// --- MAPEO DE COLORES OFICIAL DEL SISTEMA ---
const getVehicleColor = (v, isPanic) => {
    if (isPanic) return '#dc3545';       // Rojo Alerta Pánico
    if (v.is_stale) return '#adb5bd';    // Gris Sin Señal / Inactivo
    switch (v.status?.toLowerCase()) {
        case 'disponible':
            return '#28a745'; // Verde Libre / Disponible
        case 'en_route':
            return '#ffc107'; // Amarillo En Camino a Origen
        case 'arrived':
            return '#0dcaf0'; // Celeste En Posición
        case 'on_board':
            return '#0d6efd'; // Azul Pasajero a Bordo
        case 'assigned':
        case 'confirmed':
            return '#fd7e14'; // Naranja Asignado / Confirmado
        default:
            return '#28a745';
    }
};

const getVehicleBadgeStyle = (v, isPanic) => {
    if (isPanic) return { bg: 'danger', text: 'ALERTA PÁNICO' };
    if (v.is_stale) return { bg: 'secondary', text: 'SIN SEÑAL' };
    switch (v.status?.toLowerCase()) {
        case 'disponible':
            return { bg: 'success', text: 'LIBRE' };
        case 'en_route':
            return { bg: 'warning', text: 'EN CAMINO' };
        case 'arrived':
            return { bg: 'info', text: 'EN POSICIÓN' };
        case 'on_board':
            return { bg: 'primary', text: 'EN RUTA' };
        case 'assigned':
        case 'confirmed':
            return { bg: 'orange', text: 'CONFIRMADO' };
        default:
            return { bg: 'success', text: 'ONLINE' };
    }
};

const FleetMonitor = ({ isLoaded }) => {
    const location = useLocation();
    const [fleet, setFleet] = useState([]);
    const [panicAlerts, setPanicAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [map, setMap] = useState(null);
    const hasInitialCentered = useRef(false);
    const hasProcessedFocus = useRef(false);

    const loadFleetStatus = useCallback(async (isBackground = false) => {
        try {
            const [fleetRes, panicRes] = await Promise.all([
                apiClient.get('/transport/fleet/status'),
                apiClient.get('/transport/panic-alerts/')
            ]);
            setFleet(fleetRes.data);
            setPanicAlerts(panicRes.data);
        } catch (error) {
            console.error("Error cargando estado de flota:", error);
        } finally {
            if (!isBackground) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadFleetStatus();
        const interval = setInterval(() => loadFleetStatus(true), 10000);
        return () => clearInterval(interval);
    }, [loadFleetStatus]);

    // LÓGICA DE ENFOQUE AUTOMÁTICO (Desde Sala de Control)
    useEffect(() => {
        if (fleet.length > 0 && location.state?.focusDriverId && !hasProcessedFocus.current) {
            const driverToFocus = fleet.find(v => v.driver_id === location.state.focusDriverId);
            if (driverToFocus) {
                handleSelectVehicle(driverToFocus);
                hasProcessedFocus.current = true;
                hasInitialCentered.current = true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fleet, location.state]);

    useEffect(() => {
        if (isLoaded && map && fleet.length > 0 && !hasInitialCentered.current) {
            const bounds = new window.google.maps.LatLngBounds();
            fleet.forEach(v => {
                if (v.lat && v.lng) bounds.extend({ lat: Number(v.lat), lng: Number(v.lng) });
            });
            if (!bounds.isEmpty()) {
                map.fitBounds(bounds);
                hasInitialCentered.current = true;
            }
        }
    }, [isLoaded, map, fleet]);

    const handleResolvePanic = async (alertId) => {
        if (!window.confirm("¿Confirmas que la situación ha sido controlada? Se enviará una notificación de tranquilidad al conductor.")) return;
        try {
            await apiClient.put(`/transport/panic-alert/${alertId}/resolve`);
            setPanicAlerts(prev => prev.filter(a => a.id !== alertId));
            toast.success("Situación controlada. Conductor notificado.");
        } catch (e) { 
            console.error("Error resolve panic:", e);
            toast.error("Error al resolver la alerta."); 
        }
    };

    // --- MANEJO DE SELECCIÓN E INSPECCIÓN DE VEHÍCULO Y OBJETIVO ---
    const getTargetLocation = (v) => {
        if (!v) return null;
        const st = v.status?.toLowerCase();
        
        // Si va en camino a origen o ya está en origen
        if (['en_route', 'arrived', 'confirmed', 'assigned'].includes(st) && v.pickup_lat && v.pickup_lng) {
            return {
                lat: Number(v.pickup_lat),
                lng: Number(v.pickup_lng),
                label: '🟢 Origen (Recoger)',
                type: 'pickup',
                address: v.pickup_address,
                color: '#198754'
            };
        }
        
        // Si ya está con pasajero a bordo hacia destino
        if (st === 'on_board' && v.dropoff_lat && v.dropoff_lng) {
            return {
                lat: Number(v.dropoff_lat),
                lng: Number(v.dropoff_lng),
                label: '🔴 Destino (Dejar)',
                type: 'dropoff',
                address: v.dropoff_address,
                color: '#dc3545'
            };
        }

        return null;
    };

    const handleSelectVehicle = (vehicle) => {
        setSelectedVehicle(vehicle);
        if (!map || !vehicle) return;

        const target = getTargetLocation(vehicle);
        if (target && target.lat && target.lng) {
            // Ajustar mapa para ver tanto al chofer como a su punto de destino/origen
            const bounds = new window.google.maps.LatLngBounds();
            bounds.extend({ lat: Number(vehicle.lat), lng: Number(vehicle.lng) });
            bounds.extend({ lat: target.lat, lng: target.lng });
            map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
        } else {
            map.panTo({ lat: Number(vehicle.lat), lng: Number(vehicle.lng) });
            map.setZoom(16);
        }
    };

    // --- FILTRADO DE LA FLOTA EN MAPA Y SIDEBAR ---
    const filteredFleet = fleet.filter(v => {
        let matchesStatus = true;
        if (statusFilter === 'disponible') matchesStatus = v.status === 'disponible' && !v.is_stale;
        else if (statusFilter === 'en_route') matchesStatus = v.status === 'en_route';
        else if (statusFilter === 'arrived') matchesStatus = v.status === 'arrived';
        else if (statusFilter === 'on_board') matchesStatus = v.status === 'on_board';
        else if (statusFilter === 'stale') matchesStatus = v.is_stale;

        if (!matchesStatus) return false;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            const driverMatch = (v.driver_name || '').toLowerCase().includes(query);
            const mobileMatch = (v.internal_code || '').toString().toLowerCase().includes(query);
            const plateMatch = (v.vehicle_plate || '').toLowerCase().includes(query);
            const paxMatch = (v.passenger_name || '').toLowerCase().includes(query);
            return driverMatch || mobileMatch || plateMatch || paxMatch;
        }

        return true;
    });

    const activeTarget = selectedVehicle ? getTargetLocation(selectedVehicle) : null;

    if (!isLoaded) return <div className="p-5 text-center"><Spinner animation="border" variant="primary" /> <p className="mt-2">Cargando Google Maps...</p></div>;

    return (
        <div className="p-0 position-relative h-100 overflow-hidden">
            {/* CENTRAL DE ALARMAS DE PÁNICO */}
            {panicAlerts.length > 0 && (
                <div className="position-absolute top-0 w-100 p-3" style={{ zIndex: 1000 }}>
                    {panicAlerts.map(alert => (
                        <Alert key={alert.id} variant="danger" className="shadow-lg border-4 border-danger animate-pulse d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-center gap-3">
                                <div className="bg-white rounded-circle p-2 text-danger">
                                    <AlertTriangle size={32} />
                                </div>
                                <div>
                                    <h4 className="mb-0 fw-bold">🚨 ALERTA DE PÁNICO ACTIVA</h4>
                                    <p className="mb-0">
                                        Conductor: <strong>{alert.driver?.full_name || alert.user_email}</strong> 
                                        {alert.driver?.assigned_vehicle_code && <Badge bg="dark" className="ms-2">Móvil {alert.driver.assigned_vehicle_code}</Badge>}
                                    </p>
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                {alert.driver?.phone && (
                                    <>
                                        <Button variant="success" className="fw-bold" onClick={() => window.open(`https://wa.me/${alert.driver.phone.replace(/[^0-9]/g, '')}`, '_blank')}>
                                            <MessageCircle size={18} className="me-1" /> WHATSAPP
                                        </Button>
                                        <Button variant="primary" className="fw-bold" onClick={() => window.open(`tel:${alert.driver.phone}`, '_blank')}>
                                            <Phone size={18} className="me-1" /> LLAMAR
                                        </Button>
                                    </>
                                )}
                                <Button variant="dark" className="fw-bold" onClick={() => map?.panTo({ lat: Number(alert.lat), lng: Number(alert.lng) })}>
                                    <Maximize size={18} className="me-1" /> MAPA
                                </Button>
                                <Button variant="outline-light" className="fw-bold" onClick={() => handleResolvePanic(alert.id)}>
                                    <CheckCircle size={18} className="me-1" /> CONTROLADO
                                </Button>
                            </div>
                        </Alert>
                    ))}
                </div>
            )}

            <Row className="g-0">
                <Col md={9} className="position-relative">
                    {/* BARRA DE LEYENDA DE COLORES FLOTANTE EN EL MAPA */}
                    <div 
                        className="position-absolute bottom-0 start-0 m-3 p-2 bg-white rounded-3 shadow-lg border d-flex flex-wrap gap-2 align-items-center z-3"
                        style={{ fontSize: '11px', zIndex: 10 }}
                    >
                        <span className="fw-bold text-muted me-1">Estados:</span>
                        <span className="d-flex align-items-center gap-1"><span className="rounded-circle" style={{ width: 10, height: 10, backgroundColor: '#28a745' }}></span> Libre</span>
                        <span className="d-flex align-items-center gap-1"><span className="rounded-circle" style={{ width: 10, height: 10, backgroundColor: '#ffc107' }}></span> En Camino</span>
                        <span className="d-flex align-items-center gap-1"><span className="rounded-circle" style={{ width: 10, height: 10, backgroundColor: '#0dcaf0' }}></span> En Posición</span>
                        <span className="d-flex align-items-center gap-1"><span className="rounded-circle" style={{ width: 10, height: 10, backgroundColor: '#0d6efd' }}></span> Pasajero a Bordo</span>
                        <span className="d-flex align-items-center gap-1"><span className="rounded-circle" style={{ width: 10, height: 10, backgroundColor: '#adb5bd' }}></span> Sin Señal</span>
                    </div>

                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={LA_SERENA}
                        zoom={13}
                        onLoad={setMap}
                        options={{
                            styles: [
                                { "featureType": "all", "elementType": "labels.text.fill", "stylers": [{ "color": "#7c93a3" }, { "lightness": "-10" }] },
                                { "featureType": "administrative.country", "elementType": "geometry", "stylers": [{ "visibility": "on" }] },
                                { "featureType": "administrative.country", "elementType": "geometry.stroke", "stylers": [{ "color": "#a0a4a5" }] },
                                { "featureType": "landscape", "elementType": "geometry.fill", "stylers": [{ "color": "#f5f5f2" }] },
                                { "featureType": "poi", "stylers": [{ "visibility": "off" }] },
                                { "featureType": "road.highway", "elementType": "geometry.fill", "stylers": [{ "color": "#ffffff" }] },
                                { "featureType": "water", "elementType": "geometry.fill", "stylers": [{ "color": "#d3d3d3" }] }
                            ],
                            disableDefaultUI: false,
                            gestureHandling: 'greedy'
                        }}
                    >
                        <MarkerClusterer>
                            {(clusterer) =>
                                filteredFleet.map((v) => {
                                    const isPanic = panicAlerts.some(a => a.driver_id === v.driver_id);
                                    const vColor = getVehicleColor(v, isPanic);
                                    const shortName = v.driver_name ? v.driver_name.split(' ')[0] : 'Chofer';
                                    const labelText = isPanic 
                                        ? "⚠️ ALERTA" 
                                        : (v.internal_code ? `M${v.internal_code} · ${shortName}` : shortName);

                                    return (
                                        <MarkerF
                                            key={v.driver_id}
                                            position={{ lat: Number(v.lat), lng: Number(v.lng) }}
                                            clusterer={clusterer}
                                            onClick={() => handleSelectVehicle(v)}
                                            icon={{
                                                path: carIconPath,
                                                scale: 1.15,
                                                fillColor: vColor,
                                                fillOpacity: 1,
                                                strokeWeight: 1.5,
                                                strokeColor: "#ffffff",
                                                anchor: new window.google.maps.Point(12, 12),
                                                labelOrigin: new window.google.maps.Point(12, 34),
                                                rotation: 0
                                            }}
                                            label={{
                                                text: labelText,
                                                color: isPanic ? "#dc3545" : "#212529",
                                                fontWeight: "bold",
                                                fontSize: "10px",
                                                className: "bg-white px-1.5 py-0.5 rounded border shadow-sm text-nowrap"
                                            }}
                                        />
                                    );
                                })
                            }
                        </MarkerClusterer>

                        {/* --- PIN DE OBJETIVO DE VIAJE (ORIGEN O DESTINO SEGÚN EL EVENTO) --- */}
                        {activeTarget && (
                            <>
                                <MarkerF
                                    position={{ lat: activeTarget.lat, lng: activeTarget.lng }}
                                    label={{
                                        text: activeTarget.label,
                                        color: activeTarget.color,
                                        fontWeight: "bold",
                                        fontSize: "11px",
                                        className: "bg-white px-2 py-1 rounded border shadow-sm"
                                    }}
                                />
                                <Polyline
                                    path={[
                                        { lat: Number(selectedVehicle.lat), lng: Number(selectedVehicle.lng) },
                                        { lat: activeTarget.lat, lng: activeTarget.lng }
                                    ]}
                                    options={{
                                        strokeColor: activeTarget.color,
                                        strokeOpacity: 0.8,
                                        strokeWeight: 3,
                                        icons: [{
                                            icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 },
                                            offset: '0',
                                            repeat: '15px'
                                        }]
                                    }}
                                />
                            </>
                        )}

                        {/* --- INFOWINDOW DEL VEHÍCULO SELECCIONADO --- */}
                        {selectedVehicle && (
                            <InfoWindow
                                position={{ lat: Number(selectedVehicle.lat), lng: Number(selectedVehicle.lng) }}
                                onCloseClick={() => setSelectedVehicle(null)}
                            >
                                <div style={{ minWidth: '220px', padding: '4px' }}>
                                    <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                                        <h6 className="fw-bold mb-0">
                                            {selectedVehicle.internal_code ? `Móvil ${selectedVehicle.internal_code}` : 'Sin N°'} - {selectedVehicle.vehicle_plate || 'Sin Patente'}
                                        </h6>
                                        <Badge bg={getVehicleBadgeStyle(selectedVehicle, panicAlerts.some(a => a.driver_id === selectedVehicle.driver_id)).bg} className="px-2">
                                            {getVehicleBadgeStyle(selectedVehicle, panicAlerts.some(a => a.driver_id === selectedVehicle.driver_id)).text}
                                        </Badge>
                                    </div>

                                    {selectedVehicle.trip_id && (
                                        <div className="small mb-2 text-primary fw-bold bg-primary bg-opacity-10 p-1.5 rounded">
                                            <Navigation size={12} className="me-1" /> Servicio Activo: #{selectedVehicle.trip_id}
                                        </div>
                                    )}

                                    <div className="small mb-1"><User size={12} className="me-1 text-muted" /> <strong>Conductor:</strong> {selectedVehicle.driver_name}</div>
                                    {selectedVehicle.passenger_name && <div className="small mb-1"><Users size={12} className="me-1 text-muted" /> <strong>Pasajero:</strong> {selectedVehicle.passenger_name}</div>}
                                    
                                    {selectedVehicle.pickup_address && (
                                        <div className="small text-truncate mb-1" style={{ maxWidth: '240px' }} title={selectedVehicle.pickup_address}>
                                            <MapPin size={12} className="me-1 text-success" /> <strong>Origen:</strong> {selectedVehicle.pickup_address}
                                        </div>
                                    )}
                                    {selectedVehicle.dropoff_address && (
                                        <div className="small text-truncate mb-1" style={{ maxWidth: '240px' }} title={selectedVehicle.dropoff_address}>
                                            <MapPin size={12} className="me-1 text-danger" /> <strong>Destino:</strong> {selectedVehicle.dropoff_address}
                                        </div>
                                    )}

                                    <div className="small text-muted mt-2 border-top pt-2 d-flex justify-content-between align-items-center">
                                        <span><Clock size={10} className="me-1" /> {selectedVehicle.last_update ? formatDistanceToNow(new Date(selectedVehicle.last_update), { addSuffix: true, locale: es }) : 'En línea'}</span>
                                    </div>

                                    <div className="d-flex gap-1 mt-2">
                                        {selectedVehicle.driver_phone && (
                                            <Button 
                                                size="sm" 
                                                variant="success" 
                                                className="w-100 d-flex align-items-center justify-content-center gap-1 py-1" 
                                                onClick={() => window.open(`https://wa.me/${selectedVehicle.driver_phone.replace(/[^0-9]/g, '')}`, '_blank')}
                                            >
                                                <MessageCircle size={13} /> WhatsApp
                                            </Button>
                                        )}
                                        {selectedVehicle.driver_phone && (
                                            <Button 
                                                size="sm" 
                                                variant="outline-secondary" 
                                                className="py-1 px-2" 
                                                onClick={() => window.open(`tel:${selectedVehicle.driver_phone}`, '_blank')}
                                            >
                                                <Phone size={13} />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </InfoWindow>
                        )}
                    </GoogleMap>
                </Col>

                {/* SIDEBAR DE LA FLOTA */}
                <Col md={3} className="bg-light border-start overflow-auto" style={{ height: 'calc(100vh - 180px)' }}>
                    <div className="p-3 bg-white border-bottom sticky-top shadow-sm">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                <Car size={18} className="text-primary" /> Monitor de Flota
                            </h6>
                            <Badge bg="dark">{filteredFleet.length} / {fleet.length}</Badge>
                        </div>

                        {/* BUSCADOR DE CONDUCTOR O MÓVIL */}
                        <div className="mt-2 mb-2">
                            <InputGroup size="sm">
                                <InputGroup.Text className="bg-light border-end-0">
                                    <Search size={14} className="text-muted" />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Buscar por móvil o conductor..."
                                    className="border-start-0 ps-0 shadow-none fw-semibold"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                />
                                {searchQuery && (
                                    <Button 
                                        variant="outline-secondary" 
                                        className="border-start-0 border p-1 d-flex align-items-center"
                                        onClick={() => setSearchQuery('')}
                                    >
                                        <X size={14} />
                                    </Button>
                                )}
                            </InputGroup>
                        </div>

                        {/* FILTRO POR ESTADO / EVENTO */}
                        <div>
                            <Form.Select 
                                size="sm" 
                                className="fw-semibold text-secondary"
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value)}
                            >
                                <option value="all">🔍 Todos los estados ({fleet.length})</option>
                                <option value="disponible">🟢 Libres / Disponibles ({fleet.filter(f => f.status === 'disponible' && !f.is_stale).length})</option>
                                <option value="en_route">🟡 En Camino a Origen ({fleet.filter(f => f.status === 'en_route').length})</option>
                                <option value="arrived">🩵 En Posición en Origen ({fleet.filter(f => f.status === 'arrived').length})</option>
                                <option value="on_board">🔵 Pasajero a Bordo ({fleet.filter(f => f.status === 'on_board').length})</option>
                                <option value="stale">⚪ Sin Señal / Inactivos ({fleet.filter(f => f.is_stale).length})</option>
                            </Form.Select>
                        </div>
                    </div>

                    <ListGroup variant="flush">
                        {filteredFleet.map(v => {
                            const isPanic = panicAlerts.some(a => a.driver_id === v.driver_id);
                            const badgeInfo = getVehicleBadgeStyle(v, isPanic);
                            const isSelected = selectedVehicle?.driver_id === v.driver_id;

                            return (
                                <ListGroup.Item 
                                    key={v.driver_id} 
                                    action 
                                    onClick={() => handleSelectVehicle(v)}
                                    className={`border-bottom py-2.5 ${isSelected ? 'bg-primary bg-opacity-10 border-start border-4 border-primary' : ''}`}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <span className="fw-bold text-dark">
                                            {v.internal_code ? `Móvil ${v.internal_code}` : (v.vehicle_plate || 'Sin Móvil')}
                                        </span>
                                        <Badge bg={badgeInfo.bg} className="small px-2">
                                            {badgeInfo.text}
                                        </Badge>
                                    </div>
                                    <div className="small text-muted mb-1 d-flex align-items-center gap-1">
                                        <User size={12} /> {v.driver_name}
                                    </div>
                                    {v.passenger_name && (
                                        <div className="small text-primary mb-1 d-flex align-items-center gap-1 fw-semibold">
                                            <Navigation size={12} /> {v.passenger_name}
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between align-items-center mt-1">
                                        <small className="text-muted" style={{ fontSize: '10px' }}>
                                            <Clock size={10} className="me-1" />
                                            {v.last_update ? formatDistanceToNow(new Date(v.last_update), { locale: es }) : 'En línea'}
                                        </small>
                                        <Button size="sm" variant="link" className="p-0 text-decoration-none fw-bold" style={{ fontSize: '11px' }}>
                                            Ver en Mapa 📍
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            );
                        })}
                        {filteredFleet.length === 0 && !loading && (
                            <div className="p-4 text-center text-muted">
                                <Filter size={32} className="mb-2 opacity-25" />
                                <p className="small mb-0">No hay conductores en este estado.</p>
                            </div>
                        )}
                    </ListGroup>
                </Col>
            </Row>
        </div>
    );
};

export default FleetMonitor;
